import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { APPLICANT_STATUS_OPTIONS, getApplicantStatusLabel } from '../utils/applicantStatus';

const emptyForm = {
  id: null,
  name: '',
  subject: '',
  body: '',
  statusTrigger: '',
  isActive: true
};

const helperVariables = [
  '{{applicant_name}}',
  '{{applicant_email}}',
  '{{applicant_phone}}',
  '{{position}}',
  '{{lead_source}}',
  '{{status}}'
];

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadTemplates = async () => {
    try {
      const response = await axios.get('/api/email/templates');
      setTemplates(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      if (form.id) {
        await axios.put(`/api/email/templates/${form.id}`, form);
        setMessage('Template updated.');
      } else {
        await axios.post('/api/email/templates', form);
        setMessage('Template created.');
      }
      resetForm();
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    setMessage('');
    try {
      await axios.delete(`/api/email/templates/${id}`);
      if (form.id === id) {
        resetForm();
      }
      setMessage('Template deleted.');
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const previewBody = useMemo(() => (
    form.body
      .replaceAll('{{applicant_name}}', 'Jane Candidate')
      .replaceAll('{{applicant_email}}', 'jane@example.com')
      .replaceAll('{{applicant_phone}}', '+1 555 123 4567')
      .replaceAll('{{position}}', 'Sales Manager')
      .replaceAll('{{lead_source}}', 'LinkedIn')
      .replaceAll('{{status}}', getApplicantStatusLabel(form.statusTrigger || 'new_lead'))
  ), [form.body, form.statusTrigger]);

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 sm:px-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create templates that send automatically when an applicant moves into a selected status.
          </p>
        </div>

        {(message || error) && (
          <div className={`rounded-2xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {error || message}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <form onSubmit={handleSubmit} className="space-y-5 rounded-3xl border border-gray-200 bg-white px-6 py-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {form.id ? 'Edit Template' : 'Create Template'}
              </h2>
              {form.id && (
                <button type="button" onClick={resetForm} className="text-sm font-medium text-blue-600 hover:text-blue-700">
                  New template
                </button>
              )}
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-gray-700">Template Name</span>
              <input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                required
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-gray-700">Send When Status Changes To</span>
              <select
                value={form.statusTrigger}
                onChange={(event) => setForm((prev) => ({ ...prev, statusTrigger: event.target.value }))}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="">No automatic send</option>
                {APPLICANT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-gray-700">Subject</span>
              <input
                value={form.subject}
                onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                required
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-gray-700">Body</span>
              <textarea
                value={form.body}
                onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                rows={12}
                className="w-full rounded-2xl border border-gray-300 px-3 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                required
              />
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              Template is active
            </label>

            <div className="flex flex-wrap gap-2">
              {helperVariables.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, body: `${prev.body}${prev.body ? ' ' : ''}${item}` }))}
                  className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
                >
                  {item}
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving...' : form.id ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </form>

          <div className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900">Live Preview</h2>
              <p className="mt-1 text-sm text-gray-500">Preview with sample candidate data.</p>
              <div className="mt-4 rounded-2xl bg-gray-50 px-4 py-4">
                <p className="text-sm font-semibold text-gray-900">
                  {form.subject || 'Your subject will appear here'}
                </p>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-700">
                  {previewBody || 'Your email body preview will appear here'}
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900">Existing Templates</h2>
              {loading ? (
                <p className="mt-4 text-sm text-gray-500">Loading templates...</p>
              ) : templates.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">No templates yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {templates.map((template) => (
                    <div key={template.id} className="rounded-2xl border border-gray-200 px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{template.name}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Trigger: {template.statusTrigger ? getApplicantStatusLabel(template.statusTrigger) : 'Manual only'}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {template.isActive ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setForm({
                              id: template.id,
                              name: template.name,
                              subject: template.subject,
                              body: template.body,
                              statusTrigger: template.statusTrigger || '',
                              isActive: template.isActive
                            })}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(template.id)}
                            className="text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailTemplates;
